/**
 * LoginPageBuilder — Theme Configuration
 *
 * Centralized theme config derived from the actual GHL login page DOM.
 * Every field maps to CSS variables / selectors in `cssGenerator.js`.
 */

/* ── Default theme ──────────────────────────────────────── */

export const DEFAULT_THEME = {
  colors: {
    primary: '#6366f1',
    primaryHover: '#4f46e5',
    accent: '#8b5cf6',
    pageBackground: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    cardBg: '#ffffff',
    cardBorder: 'rgba(139, 92, 246, 0.15)',
    inputBg: '#f9fafb',
    inputBorder: '#d1d5db',
    inputFocusBorder: '#6366f1',
    inputFocusRing: 'rgba(99, 102, 241, 0.25)',
    textHeading: '#111827',
    textLabel: '#374151',
    textPlaceholder: '#9ca3af',
    textSecondary: '#6b7280',
    error: '#ef4444',
    success: '#10b981',
    link: '#3b82f6',
    footerText: '#6b7280',
    dividerColor: '#d1d5db',
  },

  branding: {
    logoUrl: '',
    logoHeight: '80px',
    logoWidth: '200px',
    logoObjectFit: 'contain',
    backgroundImage: '',
    backgroundMode: 'full',
    backgroundOverlay: 'rgba(0,0,0,0)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },

  signInButton: {
    bgColor: '',
    bgGradient: 'linear-gradient(135deg, var(--lp-primary) 0%, var(--lp-accent) 100%)',
    textColor: '#ffffff',
    radius: '8px',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '600',
    hoverOpacity: '0.9',
    hoverTransform: 'translateY(-1px)',
    shadow: 'none',
  },

  googleButton: {
    bgColor: '#ffffff',
    textColor: '#374151',
    borderColor: '#d1d5db',
    radius: '8px',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '500',
    hoverBg: '#f9fafb',
  },

  inputs: {
    radius: '6px',
    padding: '10px 12px',
    fontSize: '14px',
    fontWeight: '400',
    shadow: '0 1px 2px rgba(0,0,0,0.05)',
    borderWidth: '1px',
  },

  layout: {
    formPosition: 'center',
    cardWidth: '460px',
    cardRadius: '16px',
    cardShadow: '0 8px 32px rgba(0,0,0,0.12)',
    cardPadding: '32px',
    bodyMarginLeft: '55%',
    leftPanelWidth: '60%',
    leftPanelClipPath: 'polygon(0% 0%, 70% 0%, 85% 50%, 70% 100%, 0% 100%)',
    formGap: '16px',
  },

  typography: {
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    headingSize: '22px',
    headingWeight: '700',
    labelSize: '14px',
    labelWeight: '500',
    footerSize: '13px',
    forgotPasswordSize: '14px',
    dividerTextSize: '14px',
  },

  header: {
    show: false,
    bgColor: '#ffffff',
    textColor: '#374151',
    borderColor: '#e5e7eb',
  },

  customCss: '',
};

/* ── Background presets ─────────────────────────────────── */

export const BG_PRESETS = [
  { label: 'Indigo',  value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', swatch: '#667eea' },
  { label: 'Ocean',   value: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)', swatch: '#0ea5e9' },
  { label: 'Emerald', value: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', swatch: '#22c55e' },
  { label: 'Sunset',  value: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', swatch: '#f97316' },
  { label: 'Slate',   value: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', swatch: '#1e293b' },
  { label: 'Rose',    value: 'linear-gradient(135deg, #f43f5e 0%, #be123c 100%)', swatch: '#f43f5e' },
  { label: 'Amber',   value: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', swatch: '#f59e0b' },
  { label: 'Teal',    value: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)', swatch: '#14b8a6' },
  { label: 'Sky',     value: 'linear-gradient(135deg, #38bdf8 0%, #0284c7 100%)', swatch: '#38bdf8' },
  { label: 'Purple',  value: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)', swatch: '#a855f7' },
  { label: 'Dark',    value: 'linear-gradient(135deg, #111827 0%, #1f2937 100%)', swatch: '#111827' },
  { label: 'White',   value: '#f2f7fa',                                          swatch: '#f2f7fa' },
];

/* ── Section definitions for the editor UI ──────────────── */

export const SECTIONS = {
  formPosition: {
    label: 'Form Position',
    icon: 'solar:align-horizontal-center-bold',
    fields: [
      { key: 'layout.formPosition', label: 'Form Position', type: 'position-picker' },
    ],
  },

  branding: {
    label: 'Branding',
    icon: 'solar:gallery-bold',
    fields: [
      { key: 'branding.logoUrl',            label: 'Logo Image',              type: 'image', uploadKey: 'logo' },
      { key: 'branding.backgroundImage',    label: 'Background Image',        type: 'image', uploadKey: 'background' },
      { key: 'branding.backgroundMode',     label: 'Background Image Mode',   type: 'select', options: ['full','left-panel'] },
      { key: 'branding.logoHeight',         label: 'Logo Height',             type: 'text',  placeholder: '80px' },
      { key: 'branding.logoWidth',          label: 'Logo Width',              type: 'text',  placeholder: '200px' },
      { key: 'branding.logoObjectFit',      label: 'Logo Fit',               type: 'select', options: ['contain','cover','fill','none'] },
      { key: 'branding.backgroundOverlay',  label: 'Background Overlay',      type: 'text',  placeholder: 'rgba(0,0,0,0.3)' },
      { key: 'branding.backgroundSize',     label: 'Background Size',         type: 'select', options: ['cover','contain','auto','100% 100%'] },
      { key: 'branding.backgroundPosition', label: 'Background Position',     type: 'select', options: ['center','top','bottom','left','right','top left','top right','bottom left','bottom right'] },
    ],
  },

  colors: {
    label: 'Colors',
    icon: 'solar:palette-bold',
    fields: [
      { key: 'colors.primary',          label: 'Primary Color',       type: 'color' },
      { key: 'colors.primaryHover',     label: 'Primary Hover',       type: 'color' },
      { key: 'colors.accent',           label: 'Accent Color',        type: 'color' },
      { key: 'colors.textHeading',      label: 'Heading Color',       type: 'color' },
      { key: 'colors.textLabel',        label: 'Label Color',         type: 'color' },
      { key: 'colors.textSecondary',    label: 'Secondary Text',      type: 'color' },
      { key: 'colors.textPlaceholder',  label: 'Placeholder Color',   type: 'color' },
      { key: 'colors.link',             label: 'Link Color',          type: 'color' },
      { key: 'colors.error',            label: 'Error Color',         type: 'color' },
      { key: 'colors.footerText',       label: 'Footer Text',         type: 'color' },
      { key: 'colors.dividerColor',     label: 'Divider Line',        type: 'color' },
    ],
  },

  pageBackground: {
    label: 'Page Background',
    icon: 'solar:sun-fog-bold',
    fields: [
      { key: 'colors.pageBackground',       label: 'Fallback Background (gradient/solid — used when no image or behind image)', type: 'gradient' },
      { key: 'layout.leftPanelWidth',        label: 'Left Panel Width (left-panel mode only)',  type: 'text', placeholder: '60%' },
      { key: 'layout.leftPanelClipPath',     label: 'Left Panel Clip Path (left-panel mode)',   type: 'text', placeholder: 'polygon(...)' },
      { key: 'layout.bodyMarginLeft',        label: 'Form Margin Left (left-panel mode)',       type: 'text', placeholder: '55%' },
    ],
  },

  card: {
    label: 'Card',
    icon: 'solar:widget-bold',
    fields: [
      { key: 'colors.cardBg',          label: 'Card Background',    type: 'color' },
      { key: 'colors.cardBorder',      label: 'Card Border Color',  type: 'text', placeholder: 'rgba(0,0,0,0.08)' },
      { key: 'layout.cardWidth',       label: 'Card Width',         type: 'text', placeholder: '460px' },
      { key: 'layout.cardRadius',      label: 'Card Radius',        type: 'text', placeholder: '16px' },
      { key: 'layout.cardPadding',     label: 'Card Padding',       type: 'text', placeholder: '32px' },
      { key: 'layout.cardShadow',      label: 'Card Shadow',        type: 'text', placeholder: '0 8px 32px rgba(0,0,0,0.12)' },
      { key: 'layout.formGap',         label: 'Form Field Spacing',  type: 'text', placeholder: '16px' },
    ],
  },

  signInButton: {
    label: 'Sign In Button',
    icon: 'solar:cursor-bold',
    fields: [
      { key: 'signInButton.bgGradient',     label: 'Background Gradient',   type: 'text',  placeholder: 'linear-gradient(...)' },
      { key: 'signInButton.bgColor',        label: 'Background Solid',      type: 'color' },
      { key: 'signInButton.textColor',      label: 'Text Color',            type: 'color' },
      { key: 'signInButton.radius',         label: 'Border Radius',         type: 'text',  placeholder: '8px' },
      { key: 'signInButton.padding',        label: 'Padding',               type: 'text',  placeholder: '10px 16px' },
      { key: 'signInButton.fontSize',       label: 'Font Size',             type: 'text',  placeholder: '14px' },
      { key: 'signInButton.fontWeight',     label: 'Font Weight',           type: 'select', options: ['400','500','600','700','800'] },
      { key: 'signInButton.shadow',         label: 'Shadow',                type: 'text',  placeholder: 'none' },
      { key: 'signInButton.hoverOpacity',   label: 'Hover Opacity',         type: 'text',  placeholder: '0.9' },
    ],
  },

  googleButton: {
    label: 'Google / SSO Button',
    icon: 'solar:global-bold',
    fields: [
      { key: 'googleButton.bgColor',     label: 'Background',    type: 'color' },
      { key: 'googleButton.textColor',   label: 'Text Color',    type: 'color' },
      { key: 'googleButton.borderColor', label: 'Border Color',  type: 'color' },
      { key: 'googleButton.radius',      label: 'Border Radius', type: 'text', placeholder: '8px' },
      { key: 'googleButton.padding',     label: 'Padding',       type: 'text', placeholder: '10px 16px' },
      { key: 'googleButton.fontSize',    label: 'Font Size',     type: 'text', placeholder: '14px' },
      { key: 'googleButton.fontWeight',  label: 'Font Weight',   type: 'select', options: ['400','500','600','700'] },
      { key: 'googleButton.hoverBg',     label: 'Hover Background', type: 'color' },
    ],
  },

  inputs: {
    label: 'Input Fields',
    icon: 'solar:text-field-bold',
    fields: [
      { key: 'colors.inputBg',          label: 'Input Background',    type: 'color' },
      { key: 'colors.inputBorder',      label: 'Input Border',        type: 'color' },
      { key: 'colors.inputFocusBorder', label: 'Focus Border Color',  type: 'color' },
      { key: 'colors.inputFocusRing',   label: 'Focus Ring Color',    type: 'text', placeholder: 'rgba(99,102,241,0.25)' },
      { key: 'inputs.radius',           label: 'Border Radius',       type: 'text', placeholder: '6px' },
      { key: 'inputs.padding',          label: 'Padding',             type: 'text', placeholder: '10px 12px' },
      { key: 'inputs.fontSize',         label: 'Font Size',           type: 'text', placeholder: '14px' },
      { key: 'inputs.borderWidth',      label: 'Border Width',        type: 'text', placeholder: '1px' },
      { key: 'inputs.shadow',           label: 'Shadow',              type: 'text', placeholder: '0 1px 2px rgba(0,0,0,0.05)' },
    ],
  },

  typography: {
    label: 'Typography',
    icon: 'solar:text-bold',
    fields: [
      { key: 'typography.fontFamily',          label: 'Font Family',             type: 'text', placeholder: "'Inter', sans-serif" },
      { key: 'typography.headingSize',         label: 'Heading Font Size',       type: 'text', placeholder: '22px' },
      { key: 'typography.headingWeight',       label: 'Heading Font Weight',     type: 'select', options: ['400','500','600','700','800'] },
      { key: 'typography.labelSize',           label: 'Label Font Size',         type: 'text', placeholder: '14px' },
      { key: 'typography.labelWeight',         label: 'Label Font Weight',       type: 'select', options: ['400','500','600','700'] },
      { key: 'typography.footerSize',          label: 'Footer Font Size',        type: 'text', placeholder: '13px' },
      { key: 'typography.forgotPasswordSize',  label: 'Forgot Password Size',    type: 'text', placeholder: '14px' },
      { key: 'typography.dividerTextSize',     label: 'Divider Text Size',       type: 'text', placeholder: '14px' },
    ],
  },

  header: {
    label: 'Login Header Bar',
    icon: 'solar:sidebar-minimalistic-bold',
    fields: [
      { key: 'header.show',        label: 'Show Header Bar',    type: 'select', options: ['true','false'] },
      { key: 'header.bgColor',     label: 'Header Background',  type: 'color' },
      { key: 'header.textColor',   label: 'Header Text Color',  type: 'color' },
      { key: 'header.borderColor', label: 'Header Border',      type: 'color' },
    ],
  },

  custom: {
    label: 'Custom CSS',
    icon: 'solar:code-bold',
    fields: [
      { key: 'customCss', label: 'Custom CSS Overrides', type: 'textarea' },
    ],
  },
};

/* ── Helpers ─────────────────────────────────────────────── */

export function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

export function setNestedValue(obj, path, value) {
  const keys = path.split('.');
  if (keys.length === 1) return { ...obj, [keys[0]]: value };
  return {
    ...obj,
    [keys[0]]: setNestedValue(obj[keys[0]] ?? {}, keys.slice(1).join('.'), value),
  };
}
